import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { useDropzone } from 'react-dropzone';
import {
	Box,
	Button,
	Image,
	Input,
	Text,
	Heading,
	useToast,
} from '@chakra-ui/react';
import uploadIcon from '../../../assets/upload_icon.png';
import AutoTypeText from './autotype';
import { useSelector } from 'react-redux';
import { saveAs } from 'file-saver';
import excelFile from '../../../assets/master/services-purchased.csv';
import ServicesPurchasedSPaginatedData from './servicesPurchasedSPaginatedData';

const ServicesPurchased = () => {
	const toast = useToast();
	const [data, setData] = useState([]);
	const [storage, setStorage] = useState([]);
	const [sectionSample, setSectionSample] = useState([]);
	const [editing, setEditing] = useState(false);
	const locationCode = useSelector((state) => state.locationCode);
	const companyId = useSelector((state) => state.companyId);
	const [uploadMessageExcel, setUploadMessageExcel] = useState();
	const [hasValidationErrors, setHasValidationErrors] = useState(false);
	const [errors, setErrors] = useState([]);
	const [errorValue, setErrorValue] = useState();
	const [pagesWithErrors, setPagesWithErrors] = useState(new Set());
	const [currentPage, setCurrentPage] = useState(1); // Current page
	const itemsPerPage = 50; // Number of items to display per page

	function toastCallApiError() {
		return toast({
			title: 'Failed to load',
			status: 'error',
			duration: 3000,
			isClosable: true,
		});
	}
	function toastCallRequestError() {
		return toast({
			title: 'Request Failed',
			status: 'error',
			duration: 3000,
			isClosable: true,
		});
	}

	function formatDateToDdMmYy(date) {
		const day = String(date.getDate()).padStart(2, '0'); // Get day and pad with leading zero if needed
		const month = String(date.getMonth() + 1).padStart(2, '0'); // Get month (add 1 as months are zero-indexed) and pad with leading zero if needed
		const year = String(date.getFullYear()).slice(-2); // Get the last 2 digits of the year

		return `${day}-${month}-${year}`;
	}
	const currentDate = new Date();
	const formattedDate = formatDateToDdMmYy(currentDate);

	useEffect(() => {
		const rawMaterial = async () => {
			try {
				const response = await fetch(
					`${process.env.REACT_APP_API_URL}api-uom.php?company_id=${companyId}`,
					{
						method: 'GET',
					}
				);
				const response1 = await fetch(
					`${process.env.REACT_APP_API_URL}api-tds-function.php?company_id=${companyId}`,
					{
						method: 'GET',
					}
				);
				if (response.ok && response1.ok) {
					const data = await response.json();
					const data1 = await response1.json();
					setStorage(data.data);
					setSectionSample(data1.data);
				}
			} catch {
				toastCallApiError();
			}
		};
		rawMaterial();
	}, []);

	const handleFileDrop = useCallback((acceptedFiles) => {
		const file = acceptedFiles[0];
		const reader = new FileReader();

		reader.onload = (event) => {
			const data = new Uint8Array(event.target.result);
			const workbook = XLSX.read(data, { type: 'array' });

			const sheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[sheetName];

			const excelData = XLSX.utils.sheet_to_json(worksheet, {
				header: 1,
			});

			const headers = excelData[0].map((header) => header.trim());
			const formattedData = excelData.slice(1).map((row) => {
				const rowData = {};
				row.forEach((cell, columnIndex) => {
					const header = headers[columnIndex];
					rowData[header] = cell;
				});
				return rowData;
			});

			// Fill missing keys with empty strings
			const emptyRow = Object.fromEntries(
				headers.map((header) => [header, ''])
			);
			const dataWithEmptyKeys = formattedData.map((row) => ({
				...emptyRow,
				...row,
			}));

			// Filter out blank rows
			const nonBlankRows = dataWithEmptyKeys.filter((row) => {
				return Object.values(row).some(
					(cellValue) => cellValue !== null && cellValue !== ''
				);
			});
			setData(nonBlankRows);
		};

		reader.readAsArrayBuffer(file);
	});

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: handleFileDrop,
		accept: '.xlsx',
	});

	const handleDownload = () => {
		const link = document.createElement('a');
		link.href = excelFile;
		link.setAttribute(
			'download',
			`services-purchased-${locationCode}-${formattedDate}.csv`
		);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};
	// storage location
	const storageXlSX = storage;
	function generateCSVData(storageXlSX) {
		try {
			const headers = Object.keys(storageXlSX[0]); // Get the keys from the first object as headers
			const headerRow = headers.join(',') + '\n'; // CSV header row

			const rows = storageXlSX
				.map((item) => {
					const values = headers.map((header) => item[header]);
					return values.join(',');
				})
				.join('\n');

			return headerRow + rows;
		} catch {
			toastCallRequestError();
		}
	}
	const storageHandleDownload = () => {
		if (storageXlSX !== undefined) {
			const updatedData = storageXlSX?.map((asset) => ({
				...asset,
				// You can set the initial open balance value here
			}));
			const csvData = generateCSVData(updatedData);
			const blob = new Blob([csvData], {
				type: 'text/csv;charset=utf-8',
			});
			saveAs(
				blob,
				`unit-of-measurement-${locationCode}-${formattedDate}.csv`
			);
		} else {
			toastCallRequestError();
		}
	};

	// Section sample
	const sectionXlSX = sectionSample;
	function generateStorageData(sectionXlSX) {
		const headers = Object.keys(sectionXlSX[0]); // Get the keys from the first object as headers

		const data = sectionXlSX.map((item) => {
			return headers.map((header) => item[header]);
		});

		const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

		const excelBuffer = XLSX.write(workbook, {
			bookType: 'xlsx',
			type: 'array',
		});

		return excelBuffer;
	}
	const sectionHandleDownload = () => {
		if (sectionXlSX !== undefined) {
			const updatedData = sectionXlSX?.map((asset) => ({
				...asset,
			}));

			const excelData = generateStorageData(updatedData);
			const blob = new Blob([excelData], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			});

			saveAs(
				blob,
				`Section-Sample-${locationCode}-${formattedDate}.xlsx`
			);
		} else {
			toastCallRequestError();
		}
	};

	const handlePageChange = (newPage) => {
		setCurrentPage(newPage);
	};

	return (
		<Box
			width='100%'
			height='100%'
			display='flex'
			justifyContent='center'
			alignItems='center'
			flexDirection='column'>
			<Box w='100%' display='flex' justifyContent='space-around'>
				<Box
					width='38%'
					display='flex'
					justifyContent='space-around'
					alignItems='center'>
					<Box>
						<Box
							border='2px dashed var(--chakra-colors-bgGraycolor)'
							p='15px'
							borderRadius='20px'
							opacity={editing ? '0.5' : '1'}
							pointerEvents={editing ? 'none' : 'painted'}>
							<Box
								{...getRootProps()}
								bg='var(--chakra-colors-mainBlueColor)'
								width='100%'
								height='100%'
								borderRadius='20px'
								display='flex'
								justifyContent='center'
								alignItems='center'
								color='white'
								padding='50px 0px'
								cursor='grab'>
								<Input {...getInputProps()} />
								{isDragActive ? (
									<Box
										display='flex'
										flexDirection='column'
										justifyContent='center'
										alignItems='center'>
										<Image
											src={uploadIcon}
											alt='upload icon'
											height='100px'
											width='100px'
										/>
										<Text>Drop the Excel file here...</Text>
									</Box>
								) : (
									<Box
										display='flex'
										flexDirection='column'
										justifyContent='center'
										alignItems='center'>
										<Image
											src={uploadIcon}
											alt='upload icon'
											height='100px'
											width='100px'
										/>
										<Text>Drag & Drop your files</Text>
									</Box>
								)}
							</Box>
						</Box>
						<Box
							display='flex'
							width='100%'
							justifyContent='center'
							gap='15px'
							paddingTop='45px'>
							<Button
								borderRadius='10px'
								border='1px solid #003060'
								p='20px 30px'
								width='50%'
								fontSize='15px'
								background='none'
								color='mainBlueColor'
								onClick={handleDownload}
								cursor='pointer'>
								Download Sample
							</Button>
							<Button
								borderRadius='10px'
								border='1px solid #003060'
								p='20px 0px'
								width='50%'
								fontSize='15px'
								background='none'
								color='mainBlueColor'
								onClick={storageHandleDownload}
								cursor='pointer'>
								UOM Sample
							</Button>
							<Button
								borderRadius='10px'
								border='1px solid #003060'
								p='20px 0px'
								width='50%'
								fontSize='15px'
								background='none'
								color='mainBlueColor'
								onClick={sectionHandleDownload}
								cursor='pointer'>
								Section Sample
							</Button>
						</Box>
					</Box>
				</Box>

				<Box
					width='58%'
					boxShadow='rgba(0, 0, 0, 0.24) 0px 3px 8px'
					borderRadius='15px'
					padding='15px'>
					<Heading
						mb='15px'
						color='var(--chakra-colors-mainBlueColor)'
						fontSize='28px'>
						Data Summary :{' '}
					</Heading>
					<Text
						fontSize='1.7rem'
						fontWeight='600'
						marginBottom='10px'
						color='mainBlueColor'>
						Total lines - {data?.length}
					</Text>
					<Text
						fontSize='1.7rem'
						fontWeight='600'
						marginBottom='10px'
						color='red'>
						Total Error : {errorValue}
					</Text>
					<Box mt='10px' mb='10px'>
						<Box
							fontSize='1.7rem'
							fontWeight='600'
							color='mainBlueColor'>
							Pages with errors:
						</Box>

						{Array.from(pagesWithErrors).join(', ')}
					</Box>
					<Box>
						<Text
							fontSize='1.7rem'
							fontWeight='600'
							color='mainBlueColor'
							marginBottom='5px'>
							Data Review Report -
						</Text>
						<AutoTypeText />
					</Box>
				</Box>
			</Box>
			<ServicesPurchasedSPaginatedData
				data={data}
				setData={setData}
				currentPage={currentPage}
				itemsPerPage={itemsPerPage}
				editing={editing}
				setEditing={setEditing}
				onPageChange={handlePageChange}
				setErrorValue={setErrorValue}
				errors={errors}
				setErrors={setErrors}
				hasValidationErrors={hasValidationErrors}
				setHasValidationErrors={setHasValidationErrors}
				setPagesWithErrors={setPagesWithErrors}
				uploadMessageExcel={uploadMessageExcel}
				setUploadMessageExcel={setUploadMessageExcel}
			/>
		</Box>
	);
};

export default ServicesPurchased;
